/**
 * print-orchestrator — Fonctions de haut niveau qui chargent depuis la DB
 * puis délèguent à printer-router.
 *
 * Ce module est le seul qui accède aux tables sales, sale_items, products,
 * profiles, registers, tenants pour construire les TicketData.
 *
 * Contenu :
 *   - buildDemoTicketData()   — données démo déterministes (preview = print)
 *   - testPrint()             — impression de test (tous types)
 *   - printSaleReceipt()      — impression depuis saleId (charge depuis DB)
 *   - printExpenseFromSale()  — note de frais depuis saleId
 *
 * Migration Phase D — invariant 4.B.2 :
 * Les appels Supabase ont été migrés vers src/api/printing/PrintingDataApi.ts
 * et src/api/users/UsersApi.ts. Ce fichier n'importe plus supabase directement.
 *
 * Invariant 4.A.16 : les mentions NF525 sont portées par escpos-formatter.ts
 * et printer-adapters.ts — ce module ne reformate pas les mentions.
 */

import { UsersApi } from '@/api/users';
import { PrintingDataApi } from '@/api/printing/PrintingDataApi';
import type { TicketData } from './escpos-types';
import { cardLineFor } from '@/lib/cardReceipt';
import {
  findPrinter,
  loadTicketConfig,
  printReceipt,
  printKitchen,
  type KitchenData,
} from './printer-router';
import { sendViaAidl, sendViaBluetooth } from './printer-adapters';
import { printViaNetworkOrCloud } from './network-printer-adapter';
import { formatPaymentLabel, parseSplitPayments } from './payment-labels';
import { tenantAddressBlock } from './tenant-address';

// printExpenseFromSale est extrait dans expense-printing.ts (Phase 3c — split > 500 LOC).
export { printExpenseFromSale } from './expense-printing';

export type { TicketData, KitchenData };

// Libellés paiement : source unique dans ./payment-labels (formatPaymentLabel).

// ── buildDemoTicketData ──────────────────────────────────────────────────────

/**
 * Construit les données de démo utilisées pour la page Personnalisation ET
 * le bouton "Test impression" — garantit que preview et ticket imprimé sont
 * byte-identiques.
 *
 * Les quantités sont déterministes (DEMO_QTY) : plus de Math.random() qui
 * causait une dérive entre le preview et l'impression réelle.
 */
export async function buildDemoTicketData(
  tenantId: string,
  printerType: 'receipt' | 'kitchen' = 'receipt',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- configOverride est un bag de flags DB non typés
  configOverride?: Record<string, any>,
): Promise<TicketData> {
  const tenantResult = await UsersApi.getMyTenantInfo();
  const ti = tenantResult.ok ? tenantResult.value : null;
  const productsResult = await PrintingDataApi.getProductSamples(tenantId, 4);
  const products = productsResult.ok ? productsResult.value : null;

  const dbConfig = await loadTicketConfig(
    printerType === 'kitchen' ? 'cuisine' : 'caisse',
    tenantId,
  );
  const config = { ...dbConfig, ...(configOverride || {}) } as TicketData['config'];

  const fallbackItems = [
    { name: 'Café expresso', quantity: 2, unit_price: 2.5, tva_rate: 10 },
    { name: 'Croissant beurre', quantity: 3, unit_price: 1.2, tva_rate: 5.5 },
    { name: "Jus d'orange frais", quantity: 1, unit_price: 3.8, tva_rate: 10 },
  ];
  const DEMO_QTY = [2, 1, 3, 1];
  const sampleItems =
    products && products.length > 0
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any -- products vient de Supabase non typé ici
        products.map((p: any, i: number) => ({
          name: p.name,
          quantity: DEMO_QTY[i % DEMO_QTY.length],
          unit_price: Number(p.price),
          tva_rate: p.tva_rate || 20,
        }))
      : fallbackItems;

  const totalTtc = sampleItems.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  const totalHt = totalTtc / 1.1;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
  const headerSubtitle = (config as any).header_subtitle || configOverride?.header_subtitle;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
  const tvaIntracom = (config as any).tva_intracom || configOverride?.tva_intracom;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
  const headerTitle = configOverride?.header_title || (config as any).header_title;

  return {
    store_name: headerTitle || ti?.name || 'Mon Commerce',
    store_subtitle: headerSubtitle || undefined,
    store_address: tenantAddressBlock(ti),
    store_phone: ti?.phone || undefined,
    store_siret: ti?.siret || undefined,
    tva_intracom: tvaIntracom || undefined,
    ticket_number: 'PV-' + new Date().getFullYear() + '-000000',
    date: new Date().toLocaleDateString('fr-FR'),
    time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    cashier: 'Test VisionPOS',
    register: 'Caisse test',
    client: 'Jean Dupont',
    items: sampleItems,
    total_ttc: totalTtc,
    total_ht: totalHt,
    tva_breakdown: [{ rate: 10, ht: totalHt, tva: totalTtc - totalHt }],
    payment_method: 'Espèces',
    card_scheme: 'VISA',
    pan_masked: '************4242',
    auth_code: 'SIM123456',
    aid: 'A0000000031010',
    entry_mode: 'SIM',
    tid: 'SIM-TID-0001',
    mid: 'SIM-MID-0001',
    fiscal_hash: 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2',
    config,
  };
}

// ── testPrint ────────────────────────────────────────────────────────────────

/**
 * Imprime un ticket de test (tous types).
 * `configOverride` : quand appelé depuis la page Personnalisation, passe l'état
 * courant (non sauvegardé) pour que le ticket imprimé corresponde au preview.
 */
export async function testPrint(
  tenantId: string,
  registerId?: string,
  printerType: string = 'receipt',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- configOverride est un bag de flags DB non typés
  configOverride?: Partial<TicketData['config']> & Record<string, any>,
  /** Imprimante explicite (bouton « Test » d'une carte) — sinon résolution par défaut. */
  printerId?: string,
): Promise<boolean> {
  const printer = await findPrinter(tenantId, registerId, printerType, printerId);
  const ticketData = await buildDemoTicketData(
    tenantId,
    printerType === 'kitchen' ? 'kitchen' : 'receipt',
    configOverride,
  );

  if (printerType === 'kitchen') {
    return printKitchen(
      tenantId,
      registerId,
      {
        destination: 'CUISINE',
        table_number: '7',
        order_number: 'TEST-001',
        server_name: 'Test VisionPOS',
        items: ticketData.items.map((i) => ({ name: i.name, quantity: i.quantity })),
      },
      printerId,
    );
  }

  if (printerType === 'expense') {
    return _testPrintExpense(tenantId, registerId, ticketData, configOverride || {});
  }

  if (!printer) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge non typé
    const w = window as any;
    if (w.SunmiPrinter || w.SunmiInnerPrinter) return sendViaAidl(ticketData);
    throw new Error('Aucune imprimante configurée');
  }

  return printReceipt(tenantId, registerId, ticketData, printerId);
}

/**
 * Test impression note de frais. AIDL intégrée si dispo, SINON fallback
 * BT/réseau (ESC/POS) vers l'imprimante configurée — comme printExpenseFromSale.
 * Avant, ce test était AIDL-only → échouait sur une imprimante externe BT.
 */
async function _testPrintExpense(
  tenantId: string,
  registerId: string | undefined,
  demo: TicketData,
  configOverride: Partial<TicketData['config']> & Record<string, unknown>,
): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- bridge AIDL non typé
  const w = window as any;
  const b = w.SunmiPrinter || w.SunmiInnerPrinter;

  const mergedConfig = { ...demo.config, ...configOverride };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
  const cpl = (mergedConfig as any).largeur_papier === 58 ? 32 : 48;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
  const rawPolice = (mergedConfig as any).police_size;
  const ps = Math.max(14, Math.min(22, rawPolice ? rawPolice - 2 : 18));
  const sep = '-'.repeat(cpl);
  const dsep = '='.repeat(cpl);
  const mealCount = 2;
  const totalTtc = demo.total_ttc;
  const perPerson = totalTtc / mealCount;
  const padLine = (l: string, r: string) => {
    const maxLeft = cpl - r.length - 1;
    const trimmed = l.length > maxLeft ? l.substring(0, maxLeft) : l;
    return trimmed + ' '.repeat(Math.max(1, cpl - trimmed.length - r.length)) + r;
  };

  // ── AIDL intégrée (Sunmi D3/D3Mini) ──
  if (b) {
    for (let i = 0; i < 5; i++) {
      try {
        if (b.isConnected?.()) break;
      } catch {
        /* ignore */
      }
      try {
        b.init?.();
      } catch {
        /* ignore */
      }
      await new Promise((r) => setTimeout(r, 400 + i * 200));
    }
    try {
      b.beginTransaction();
      b.setAlignment(1);
      b.setBold(true);
      b.setFontSize(ps + 4);
      b.printText(demo.store_name);
      b.setBold(false);
      b.setFontSize(ps);
      if (demo.store_address) b.printText(demo.store_address);
      if (demo.store_siret) b.printText('SIRET : ' + demo.store_siret);
      b.printText(sep);
      b.setBold(true);
      b.setFontSize(ps + 4);
      b.printText('NOTE DE FRAIS');
      b.setBold(false);
      b.setFontSize(ps);
      b.printText('TEST ' + (demo.ticket_number || 'DEMO'));
      b.printText(new Date().toLocaleString('fr-FR'));
      b.printText('Salarie : Test Demo');
      b.printText('Couverts : ' + mealCount);
      b.printText(sep);
      b.setAlignment(0);
      for (const item of demo.items) {
        const name = item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name;
        b.printText(padLine(name, (item.quantity * item.unit_price).toFixed(2) + ' EUR'));
      }
      b.printText(dsep);
      b.setBold(true);
      b.setFontSize(ps + 3);
      b.printText(padLine('TOTAL TTC', totalTtc.toFixed(2) + ' EUR'));
      b.setBold(false);
      b.setFontSize(ps);
      b.printText(padLine('Par personne', perPerson.toFixed(2) + ' EUR'));
      b.printText(dsep);
      b.setAlignment(1);
      b.setFontSize(ps - 2);
      b.printText('Justificatif note de frais');
      b.printText('TVA non recuperable pour particuliers');
      b.feedLines(3);
      b.commitTransaction();
      return true;
    } catch (err) {
      console.error('[PrintService] testPrintExpense AIDL error:', err);
      return false;
    }
  }

  // ── Fallback BT/réseau (imprimante externe configurée) ──
  const printer = await findPrinter(tenantId, registerId, 'receipt');
  if (!printer) throw new Error('Aucune imprimante configurée pour le test Note de frais');
  const ESC_INIT = '\x1B@';
  const ALIGN_C = '\x1B\x61\x01';
  const ALIGN_L = '\x1B\x61\x00';
  const BOLD_ON = '\x1B\x45\x01';
  const BOLD_OFF = '\x1B\x45\x00';
  const SIZE_L = '\x1D\x21\x10';
  const SIZE_N = '\x1D\x21\x00';
  const CUT = '\x1D\x56\x01';
  let t = ESC_INIT + ALIGN_C + BOLD_ON + SIZE_L + demo.store_name + '\n' + SIZE_N + BOLD_OFF;
  if (demo.store_address) t += demo.store_address + '\n';
  if (demo.store_siret) t += 'SIRET : ' + demo.store_siret + '\n';
  t += sep + '\n' + BOLD_ON + SIZE_L + 'NOTE DE FRAIS\n' + SIZE_N + BOLD_OFF;
  t += 'TEST ' + (demo.ticket_number || 'DEMO') + '\n';
  t += new Date().toLocaleString('fr-FR') + '\n';
  t += 'Salarie : Test Demo\nCouverts : ' + mealCount + '\n' + sep + '\n' + ALIGN_L;
  for (const item of demo.items) {
    const name = item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name;
    t += padLine(name, (item.quantity * item.unit_price).toFixed(2) + ' EUR') + '\n';
  }
  t += dsep + '\n' + BOLD_ON + SIZE_L + padLine('TOTAL TTC', totalTtc.toFixed(2) + ' EUR') + '\n';
  t +=
    SIZE_N + BOLD_OFF + padLine('Par personne', perPerson.toFixed(2) + ' EUR') + '\n' + dsep + '\n';
  t += ALIGN_C + 'Justificatif note de frais\nTVA non recuperable pour particuliers\n\n\n\n' + CUT;
  const bytes = new TextEncoder().encode(t);
  if (printer.connection_type === 'bluetooth' && printer.bt_mac_address) {
    return sendViaBluetooth(printer.bt_mac_address, bytes);
  }
  if (printer.connection_type === 'network' && printer.ip_address) {
    // Socket natif sur l'APK (l'Edge Function cloud ne joint pas une IP privée).
    return printViaNetworkOrCloud(printer.ip_address, printer.port || 9100, bytes);
  }
  throw new Error('Imprimante configurée incompatible pour le test Note de frais');
}

// ── printSaleReceipt ─────────────────────────────────────────────────────────

/**
 * Charge une vente depuis la DB et imprime son ticket de caisse.
 * Gère : chargement tenant, vendeur, caisse, config ticket, TVA, auto-QR review.
 */
export async function printSaleReceipt(
  tenantId: string,
  saleId: string,
  options?: { registerId?: string; isDuplicate?: boolean },
): Promise<boolean> {
  const registerId = options?.registerId;
  const isDuplicate = options?.isDuplicate ?? false;

  // Vague 1 — toutes les lectures à clés connues d'avance (saleId / tenantId)
  // en UN seul aller-retour parallèle : sale, items, info tenant, extra tenant
  // (slug/review) et config ticket. Avant, extra-tenant et loadTicketConfig
  // étaient des await séquentiels → cascade qui rallongeait le délai
  // d'impression post-refacto. Aucun changement de contenu du ticket.
  const [saleRes, itemsRes, tenantRes, extraRes, config, detailedCard] = await Promise.all([
    PrintingDataApi.getSale(saleId),
    PrintingDataApi.getSaleItems(saleId),
    UsersApi.getMyTenantInfo(),
    PrintingDataApi.getTenantSlugAndReview(tenantId),
    loadTicketConfig('caisse', tenantId),
    PrintingDataApi.isOptInFeatureEnabled(tenantId, 'retour_tpe_detaille'),
  ]);

  const sale = saleRes.ok ? saleRes.value : null;
  if (!sale) throw new Error('Vente non trouvée');
  const items = itemsRes.ok ? itemsRes.value : [];
  const tenantRaw = tenantRes.ok ? tenantRes.value : null;

  let tenant: typeof tenantRaw & { slug?: string | null; google_review_url?: string | null } =
    tenantRaw;
  if (tenantRaw && extraRes.ok && extraRes.value) {
    tenant = { ...tenantRaw, ...extraRes.value };
  }

  // Vague 2 — cashier + register dépendent de la vente (vague 1).
  let cashier = 'Caissier';
  let register = 'Caisse 1';
  try {
    if (sale.user_id) {
      const cashierRes = await PrintingDataApi.getCashierName(sale.user_id);
      if (cashierRes.ok && cashierRes.value) cashier = cashierRes.value;
    }
    if (sale.register_id) {
      const registerRes = await PrintingDataApi.getRegisterName(sale.register_id);
      if (registerRes.ok && registerRes.value) register = registerRes.value;
    }
  } catch {
    /* garder les défauts */
  }

  // Calcul TVA avec remise globale (bug historique avant 2026-04-30)
  const discountFactor =
    1 - (Number((sale as { discount_percent?: number }).discount_percent) || 0) / 100;
  const tvaMap = new Map<number, { ht: number; tva: number }>();
  for (const item of items) {
    const rate = item.tva_rate || 20;
    const ttc = item.quantity * Number(item.unit_price) * discountFactor;
    const ht = ttc / (1 + rate / 100);
    const tva = ttc - ht;
    const prev = tvaMap.get(rate) || { ht: 0, tva: 0 };
    tvaMap.set(rate, { ht: prev.ht + ht, tva: prev.tva + tva });
  }
  const totalHt = [...tvaMap.values()].reduce((s, v) => s + v.ht, 0);

  const LEGACY_SUBTITLE_PLACEHOLDER = '1 rue du Commerce, 75001 Paris';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
  const subtitle = (config as any).header_subtitle;
  const cleanSubtitle =
    subtitle && subtitle.trim() !== LEGACY_SUBTITLE_PLACEHOLDER ? subtitle : undefined;

  const autoReviewConfig: Record<string, unknown> = { ...(config as Record<string, unknown>) };
  if (tenant?.google_review_url && tenant?.slug) {
    autoReviewConfig.show_qr_code = true;
    autoReviewConfig.qr_code_content = `https://pos.visionpos.fr/review/${tenant.slug}?sale=${saleId}`;
  }

  const ticketData: TicketData = {
    isDuplicate,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
    store_name: (config as any).header_title || tenant?.name || 'VisionPOS',
    store_subtitle: cleanSubtitle,
    store_address: tenantAddressBlock(tenant),
    store_phone: tenant?.phone || undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
    store_email: (config as any).show_email ? tenant?.email : undefined,
    store_siret: tenant?.siret || undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- flags DB non typés
    tva_intracom: (config as any).tva_intracom || undefined,
    ticket_number: sale.ticket_number || undefined,
    date: new Date(sale.created_at).toLocaleDateString('fr-FR'),
    time: new Date(sale.created_at).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
    cashier,
    register,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- sale_items non typés
    items: items.map((i: any) => ({
      name: i.product_name,
      quantity: i.quantity,
      unit_price: Number(i.unit_price),
      tva_rate: i.tva_rate || 20,
    })),
    discount_percent: Number(sale.discount_percent) || 0,
    total_ttc: Number(sale.total),
    total_ht: totalHt,
    tva_breakdown: [...tvaMap.entries()].map(([rate, v]) => ({ rate, ht: v.ht, tva: v.tva })),
    payment_method: formatPaymentLabel(sale.payment_method),
    split_payments: parseSplitPayments((sale as { split_payments?: unknown }).split_payments),
    card_scheme: sale.card_scheme || undefined,
    pan_masked: sale.pan_masked || undefined,
    auth_code: sale.auth_code || undefined,
    aid: sale.aid || undefined,
    entry_mode: sale.entry_mode || undefined,
    tid: sale.tid || undefined,
    mid: sale.mid || undefined,
    card_line: cardLineFor(sale, detailedCard, config),
    fiscal_hash: sale.fiscal_hash || undefined,
    config: autoReviewConfig as TicketData['config'],
  };

  return printReceipt(tenantId, registerId || sale.register_id, ticketData);
}

// ── printExpenseFromSale ─────────────────────────────────────────────────────

// printExpenseFromSale est ré-exportée depuis ./expense-printing (Phase 3c).
